import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  FileSpreadsheet,
  FileText,
  FilePlus2,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
} from "lucide-react";

/**
 * Table export / import panel.
 *
 * Exports include human-readable Greek headers, ordered columns
 * (readable fields first, ids last) and a companion "→ Όνομα" column
 * next to every foreign-key id, populated via a server-side lookup.
 *
 * Imports flow through a two-step wizard:
 *   1. Select file → dry-run preview (rows added / updated, warnings)
 *   2. Enter admin_code → actual import.
 * The preview also resolves FKs by name (auto-fills a missing
 * `*_id` when only the `*_name` column is filled) so a staff member
 * can edit the exported file with human-readable references.
 */
export default function ImportExportPanel({ supervisorMode = false }) {
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState("products");
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows_in_file, would_add, ... }

  const load = useCallback(async () => {
    try {
      const r = await http.get("/tables/list");
      setTables(r.data.items || []);
    } catch {
      toast.error("Σφάλμα φόρτωσης πινάκων");
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, [load]);

  const onExport = async (fmt) => {
    setBusy(true);
    try {
      const r = await http.get(`/tables/${selected}/export?fmt=${fmt}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: r.headers["content-type"] || "application/octet-stream",
      });
      const cd = r.headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const name = match ? match[1] : `${selected}.${fmt}`;
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Εξαγωγή: ${name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Αποτυχία εξαγωγής");
    } finally {
      setBusy(false);
    }
  };

  const onTemplate = async () => {
    setBusy(true);
    try {
      const r = await http.get(`/tables/${selected}/template`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: r.headers["content-type"] || "application/octet-stream",
      });
      const cd = r.headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const name = match ? match[1] : `${selected}-template.xlsx`;
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Πρότυπο: ${name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Αποτυχία λήψης προτύπου");
    } finally {
      setBusy(false);
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setPreview(null);
    setPendingFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post(
        `/tables/${selected}/import-preview?mode=${mode}`,
        fd,
      );
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Αποτυχία preview");
      setPendingFile(null);
    } finally {
      setBusy(false);
    }
  };

  const cancelPreview = () => {
    setPendingFile(null);
    setPreview(null);
  };

  const doImport = async () => {
    if (!pendingFile) return;
    let code = "";
    if (!supervisorMode) {
      code = (await promptDialog("Κωδικός διαχειριστή για επιβεβαίωση:"));
      if (!code) return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("admin_code", code);
      const r = await http.post(
        `/tables/${selected}/import?mode=${mode}`,
        fd,
      );
      toast.success(
        `Εισήχθησαν ${r.data.inserted} εγγραφές (${r.data.mode})` +
          (r.data.warnings?.length
            ? ` — ${r.data.warnings.length} προειδοποιήσεις`
            : ""),
      );
      cancelPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Αποτυχία εισαγωγής");
    } finally {
      setBusy(false);
    }
  };

  const selectedLabel =
    tables.find((t) => t.name === selected)?.label || selected;

  return (
    <Card className="p-6 space-y-4" data-testid="import-export-panel">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
        <FileSpreadsheet size={18} /> Εισαγωγή / Εξαγωγή Πινάκων
      </h3>

      <p className="text-xs text-slate-500 leading-relaxed">
        Το export περιέχει ελληνικές κεφαλίδες, ταξινομημένες στήλες
        και δίπλα σε κάθε ID (π.χ. <b>Κατηγορία</b>, <b>Εκτυπωτής</b>)
        μια στήλη «<b>→ Όνομα</b>» για εύκολη ανάγνωση. Στο import μπορείς
        να αφήσεις το ID κενό και να συμπληρώσεις μόνο το όνομα — το
        σύστημα κάνει αυτόματο lookup. Για νέο πίνακα, χρησιμοποίησε
        το κουμπί <b>«Πρότυπο (Empty XLSX)»</b> που κατεβάζει ένα άδειο
        αρχείο με έτοιμες κεφαλίδες + ξεχωριστό sheet «Αναφορές» με τα
        διαθέσιμα ονόματα.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-xs">Πίνακας</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-9" data-testid="impexp-table-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tables.map((t) => (
                <SelectItem key={t.name} value={t.name}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Mode Εισαγωγής</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="h-9" data-testid="impexp-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="append">Προσθήκη (χωρίς διαγραφή)</SelectItem>
              <SelectItem value="replace">Αντικατάσταση (διαγραφή όλων)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] text-slate-500 flex items-start gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded">
          <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
          Σε «Αντικατάσταση», όλα τα τρέχοντα δεδομένα του πίνακα
          διαγράφονται. Απαιτείται προεπισκόπηση + κωδικός διαχειριστή.
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          onClick={onTemplate}
          disabled={busy}
          variant="outline"
          className="h-9 border-blue-300 text-blue-700 hover:bg-blue-50"
          data-testid="impexp-template-btn"
        >
          <FilePlus2 size={14} className="mr-1.5" /> Πρότυπο (Empty XLSX)
        </Button>
        <Button
          onClick={() => onExport("xlsx")}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700 h-9"
          data-testid="impexp-export-xlsx"
        >
          <ArrowDownToLine size={14} className="mr-1.5" /> Εξαγωγή XLSX
        </Button>
        <Button
          onClick={() => onExport("csv")}
          disabled={busy}
          variant="outline"
          className="h-9"
          data-testid="impexp-export-csv"
        >
          <FileText size={14} className="mr-1.5" /> Εξαγωγή CSV
        </Button>
        <label
          className={`inline-flex items-center gap-1.5 h-9 px-3 border-2 rounded-md cursor-pointer text-sm font-bold ${
            mode === "replace"
              ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
              : "border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100"
          } ${busy ? "opacity-60 pointer-events-none" : ""}`}
        >
          {busy ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <ArrowUpFromLine size={14} />
          )}
          {busy
            ? "Επεξεργασία..."
            : `Εισαγωγή (${mode === "replace" ? "Αντικατάσταση" : "Προσθήκη"})`}
          <input
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            onChange={onFilePicked}
            disabled={busy}
            data-testid="impexp-import-input"
          />
        </label>
      </div>

      <ImportPreviewDialog
        open={!!preview}
        onClose={cancelPreview}
        onConfirm={doImport}
        preview={preview}
        fileName={pendingFile?.name || ""}
        tableLabel={selectedLabel}
        mode={mode}
        busy={busy}
      />
    </Card>
  );
}

function ImportPreviewDialog({
  open,
  onClose,
  onConfirm,
  preview,
  fileName,
  tableLabel,
  mode,
  busy,
}) {
  if (!preview) return null;
  const isReplace = mode === "replace";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="import-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info size={18} className="text-blue-600" />
            Προεπισκόπηση Εισαγωγής
          </DialogTitle>
          <DialogDescription>
            Πίνακας <b>{tableLabel}</b> από <b>{fileName}</b> — Λειτουργία{" "}
            <b>{isReplace ? "ΑΝΤΙΚΑΤΑΣΤΑΣΗ" : "ΠΡΟΣΘΗΚΗ"}</b>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-2">
          <PreviewStat
            label="Γραμμές στο αρχείο"
            value={preview.rows_in_file}
            color="slate"
          />
          <PreviewStat
            label="Θα προστεθούν"
            value={preview.would_add}
            color="emerald"
          />
          <PreviewStat
            label="Θα ενημερωθούν"
            value={preview.would_update}
            color="amber"
            hide={isReplace}
          />
          <PreviewStat
            label="Θα διαγραφούν"
            value={preview.would_delete}
            color="rose"
            hide={!isReplace || preview.would_delete === 0}
          />
        </div>

        <div className="text-xs text-slate-500 border-t pt-2">
          Τρέχουσες εγγραφές πίνακα: <b>{preview.current_row_count}</b>
        </div>

        {preview.warnings?.length > 0 && (
          <div
            className="border border-amber-200 bg-amber-50 rounded p-3 text-xs space-y-1 max-h-48 overflow-auto"
            data-testid="import-preview-warnings"
          >
            <div className="font-bold text-amber-800 flex items-center gap-1.5">
              <AlertTriangle size={13} /> Προειδοποιήσεις (
              {preview.warning_count})
            </div>
            {preview.warnings.slice(0, 20).map((w, i) => (
              <div key={i} className="text-amber-800 pl-4">
                • {w}
              </div>
            ))}
            {preview.warning_count > 20 && (
              <div className="text-amber-700 italic pl-4">
                (…και {preview.warning_count - 20} ακόμη)
              </div>
            )}
          </div>
        )}

        {preview.warnings?.length === 0 && (
          <div className="text-xs text-emerald-700 flex items-center gap-1.5">
            <CheckCircle2 size={14} /> Δεν εντοπίστηκαν προειδοποιήσεις.
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="import-preview-cancel"
          >
            Ακύρωση
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className={
              isReplace
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }
            data-testid="import-preview-confirm"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin mr-1.5" />
            ) : (
              <ArrowUpFromLine size={14} className="mr-1.5" />
            )}
            {isReplace
              ? "Επιβεβαίωση Αντικατάστασης"
              : "Επιβεβαίωση Εισαγωγής"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewStat({ label, value, color, hide }) {
  if (hide) return null;
  const map = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`p-3 rounded border ${map[color] || map.slate}`}>
      <div className="text-3xl font-mono font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold mt-1">
        {label}
      </div>
    </div>
  );
}
